import React, { useState, useEffect, useMemo } from "react";
import "./Logs.css";
import { ToastType } from "../../Components/Toast/Toast";
import { useToast } from "../../Components/Toast/ToastContext";
import Button from "../../ui/Button";

export interface APIResponse<T> {
  success: boolean;
  code: number;
  data?: T;
  error?: string;
}

export interface Log {
  logId: number;
  subcategory: string;
  log: string;
  logTimestamp: string;
}

interface LogViewProps {
  response?: APIResponse<Log[]>;
  isLoading?: boolean;
}

const formatLog = (log: Log) => {
  const formattedTimestamp = `[${new Date(log.logTimestamp).toLocaleString()}]`;
  return `${formattedTimestamp} ${log.log}`;
};

const highlightText = (text: string, query: string) => {
  if (!query.trim()) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="log-highlight">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

const LogView: React.FC<LogViewProps> = ({ response, isLoading }) => {
  const { showToast } = useToast();
  const [isCopied, setIsCopied] = useState(false);
  const [logText, setLogText] = useState("");
  const [localSearch, setLocalSearch] = useState("");
  const [reversed, setReversed] = useState(false);

  const handleCopyClick = () => {
    if (logText) {
      navigator.clipboard
        .writeText(logText)
        .then(() => {
          showToast(ToastType.Success, "Log content copied to clipboard", 5);
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 1500);
        })
        .catch(() => {
          showToast(ToastType.Error, "Failed to copy log content.", 5);
        });
    }
  };

  const handleDownload = () => {
    if (!logText) return;
    const blob = new Blob([logText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `logs_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(ToastType.Success, "Log file downloaded.", 5);
  };

  useEffect(() => {
    if (response && response.success) {
      const logs = response.data || [];
      const logContent = logs.map(formatLog);
      setLogText(logContent.join("\n"));
    }
    setLocalSearch("");
  }, [response]);

  const filteredLogs = useMemo(() => {
    let logs = response?.data || [];
    if (localSearch.trim()) {
      const q = localSearch.toLowerCase();
      logs = logs.filter(
        (log) =>
          log.log.toLowerCase().includes(q) ||
          log.subcategory.toLowerCase().includes(q)
      );
    }
    if (reversed) {
      logs = [...logs].reverse();
    }
    return logs;
  }, [response, localSearch, reversed]);

  if (isLoading) {
    return (
      <div className="log-view">
        <div className="log-loading">
          <div className="log-spinner" />
          <p>Fetching logs...</p>
        </div>
      </div>
    );
  }

  if (!response) {
    return null;
  }

  if (!response.success) {
    return (
      <div className="log-view log-error-card">
        {response.error || "Error fetching logs."}
      </div>
    );
  }

  const totalLogs = response.data?.length || 0;

  return (
    <div className="log-view">
      <div className="log-toolbar">
        <div className="log-toolbar-left">
          <span className="log-total">
            {filteredLogs.length === totalLogs
              ? `${totalLogs} logs`
              : `${filteredLogs.length} / ${totalLogs} logs`}
          </span>
        </div>
        <div className="log-toolbar-right">
          <Button onClick={() => setReversed((r) => !r)}>
            {reversed ? "Oldest First" : "Newest First"}
          </Button>
          <input
            className="log-local-search"
            type="text"
            placeholder="Filter results..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
          />
          <Button onClick={handleCopyClick}>
            {isCopied ? "Copied!" : "Copy"}
          </Button>
          <Button onClick={handleDownload}>Download</Button>
        </div>
      </div>

      {filteredLogs.length === 0 ? (
        <div className="log-empty">
          {totalLogs === 0
            ? "No logs found for the selected filters and date range."
            : "No logs match your local filter."}
        </div>
      ) : (
        <div className="log-list">
          {filteredLogs.map((log) => (
            <div key={log.logId} className="log-item">
              <div className="log-item-header">
                <span className="log-subcategory">
                  {highlightText(log.subcategory, localSearch)}
                </span>
                <span className="log-id">#{log.logId}</span>
              </div>
              <div className="log-timestamp">
                {highlightText(formatLog(log), localSearch)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LogView;
